import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../../Navbar";
import {
  publicRoomsUrl, guestReservationsUrl, ASSETS,
} from "../../BaseURL";
import "../../styles/public.css";

// Status labels visitors actually see.  We hide internal staff statuses
// like "cleaning" / "maintenance" — those rooms aren't browseable, and
// "out_of_order" is filtered server-side already.
const STATUS_LABEL = {
  available:   "Available",
  reserved:    "Held",
  occupied:    "In Use",
  cleaning:    "Servicing",
  maintenance: "Servicing",
};
// Only "available" rooms are bookable.  Everything else is shown but the
// CTA is disabled so the page stays informative rather than misleading.
const IS_BOOKABLE = (status) => status === "available";

const AMENITY_LABEL = {
  wifi:"Wi-Fi", ac:"Air Cond.", tv:"Smart TV", mini_bar:"Mini Bar",
  safe:"Safe", hair_dryer:"Hair Dryer", bathtub:"Bathtub", shower:"Hot Shower",
  balcony:"Balcony", kitchenette:"Kitchenette", workspace:"Workspace",
  coffee:"Coffee", iron:"Iron", breakfast:"Breakfast",
  pet_friendly:"Pet Friendly", smoking:"Smoking",
};

// Yesterday-proof today (YYYY-MM-DD) for the date input min attribute.
const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const tomorrowISO = () => {
  const d = new Date(); d.setDate(d.getDate() + 1);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const parseAmenities = (raw) =>
  (raw || "").split(",").map((s) => s.trim()).filter(Boolean);

export default function Reserve() {
  const navigate = useNavigate();
  const guest    = JSON.parse(localStorage.getItem("guest") || "null");

  const [rooms,        setRooms]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState("");
  const [selected,     setSelected]     = useState(null);
  const [confirmation, setConfirmation] = useState(null);     // { code, total, nights }

  // Filters
  const [typeFilter, setTypeFilter] = useState("all");
  const [search,     setSearch]     = useState("");

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await axios.get(publicRoomsUrl);
      setRooms(res.data?.results || []);
    } catch (e) {
      console.error(e);
      setErr("Could not load rooms. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const types = useMemo(() => {
    const set = new Set(rooms.map((r) => r.room_type_name).filter(Boolean));
    return Array.from(set).sort();
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (typeFilter !== "all" && r.room_type_name !== typeFilter) return false;
      if (!term) return true;
      return (
        (r.room_number || "").toLowerCase().includes(term) ||
        (r.room_type_name || "").toLowerCase().includes(term) ||
        (r.view_type || "").toLowerCase().includes(term) ||
        (r.description || "").toLowerCase().includes(term)
      );
    });
  }, [rooms, typeFilter, search]);

  return (
    <div className="pp-root">
      <div className="pp-bg" />
      <Navbar />

      <div className="pp-content">
        {/* Header */}
        <div className="pp-page-head">
          <div className="pp-eyebrow">Stay With Us</div>
          <h1 className="pp-h1">
            Reserve <em>your room</em>
          </h1>
          <p className="pp-tag">
            Browse our rooms, pick the one that suits you, and reserve in a few clicks.
            We'll confirm as soon as payment is received.
          </p>
        </div>

        {/* Filters */}
        <div className="pp-filter-row">
          <input
            placeholder="Search by room number, type, view…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All room types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {guest ? (
            <Link to="/my-reservations" className="pp-btn-ghost"
                  style={{ width: "auto", padding: "9px 18px", fontSize: 11 }}>
              My Reservations
            </Link>
          ) : (
            <Link to="/guest/login" state={{ next: "/reserve" }}
                  className="pp-btn-ghost"
                  style={{ width: "auto", padding: "9px 18px", fontSize: 11 }}>
              Sign in to book
            </Link>
          )}
        </div>

        {err && <div className="pp-error" style={{ maxWidth: 720, margin: "0 auto 20px" }}>{err}</div>}

        {loading ? (
          <div className="pp-loading">Loading rooms…</div>
        ) : filteredRooms.length === 0 ? (
          <div className="pp-loading">No rooms match.</div>
        ) : (
          <div className="pp-room-grid">
            {filteredRooms.map((r) => <RoomCard key={r.id} room={r} onClick={() => setSelected(r)} />)}
          </div>
        )}
      </div>

      {selected && (
        <RoomDetailModal
          room={selected}
          guest={guest}
          onClose={() => setSelected(null)}
          onSignIn={() => {
            // Take the visitor to sign-in, then return them to /reserve.
            navigate("/guest/login", { state: { next: "/reserve" } });
          }}
          onReserved={(payload) => {
            setSelected(null);
            setConfirmation(payload);
            load();         // refresh statuses so the booked room reflects
          }}
        />
      )}

      {confirmation && (
        <ConfirmationModal
          payload={confirmation}
          onClose={() => setConfirmation(null)}
          onViewMine={() => { setConfirmation(null); navigate("/my-reservations"); }}
        />
      )}
    </div>
  );
}

// ─── Pretty room card on the public grid ─────────────────────────────────
function RoomCard({ room, onClick }) {
  const photo  = room.image_url ? `${ASSETS}${room.image_url}` : null;
  const busy   = !IS_BOOKABLE(room.status);
  const status = STATUS_LABEL[room.status] || room.status;

  return (
    <div className="pp-room-card" onClick={onClick}>
      <div className="pp-room-photo"
           style={photo ? { backgroundImage: `url(${photo})` } : undefined}>
        {!photo && <div className="pp-room-photo-empty">◇</div>}
        <div className={`pp-room-status ${busy ? "busy" : ""}`}>{status}</div>
      </div>
      <div className="pp-room-body">
        <div className="pp-room-name">Room {room.room_number}</div>
        <div className="pp-room-type">
          {room.room_type_name || "Standard"}
          {room.floor ? ` · Floor ${room.floor}` : ""}
        </div>

        <div className="pp-room-meta">
          {room.max_occupancy ? <span>👥 {room.max_occupancy}</span> : null}
          {room.bed_type      ? <span>🛏 {room.bed_type}</span> : null}
          {room.view_type     ? <span>🪟 {room.view_type}</span> : null}
        </div>

        {room.description && <div className="pp-room-desc">{room.description}</div>}

        <div className="pp-room-foot">
          <div className="pp-room-price">
            GH₵ {Number(room.price_per_night || 0).toFixed(0)}
            <small>/ night</small>
          </div>
          <div className="pp-room-cta">View · Reserve →</div>
        </div>
      </div>
    </div>
  );
}

// ─── Detail + booking modal ──────────────────────────────────────────────
function RoomDetailModal({ room, guest, onClose, onSignIn, onReserved }) {
  const photo = room.image_url ? `${ASSETS}${room.image_url}` : null;
  const amenities = parseAmenities(room.amenities);
  const bookable  = IS_BOOKABLE(room.status);

  const [checkIn,  setCheckIn]  = useState(todayISO());
  const [checkOut, setCheckOut] = useState(tomorrowISO());
  const [adults,   setAdults]   = useState(1);
  const [children, setChildren] = useState(0);
  const [notes,    setNotes]    = useState("");
  const [busy,     setBusy]     = useState(false);
  const [err,      setErr]      = useState("");

  // Live quote — nights × rate.  Mirrors the backend's nightly billing
  // so visitors see the same number the server will compute.
  const quote = useMemo(() => {
    if (!checkIn || !checkOut) return { nights: 0, total: 0 };
    const a = new Date(checkIn + "T00:00:00");
    const b = new Date(checkOut + "T00:00:00");
    const nights = Math.max(0, Math.round((b - a) / 86400000));
    const rate   = Number(room.price_per_night) || 0;
    return { nights, total: nights * rate };
  }, [checkIn, checkOut, room.price_per_night]);

  const submit = async (e) => {
    e.preventDefault();
    if (!guest) return onSignIn();
    if (quote.nights <= 0) {
      setErr("Pick a check-out date that's after the check-in date.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const res = await axios.post(guestReservationsUrl, {
        room_id:        room.id,
        check_in_date:  checkIn,
        check_out_date: checkOut,
        adults, children, notes,
      });
      if (res.data?.success) {
        onReserved({
          code:   res.data.code,
          total:  res.data.total_amount,
          nights: res.data.nights,
          room_number: room.room_number,
          check_in:  checkIn,
          check_out: checkOut,
        });
      } else {
        setErr(res.data?.message || "Could not submit reservation.");
      }
    } catch (e) {
      setErr(e.response?.data?.message || "Could not submit reservation.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pp-modal-overlay" onClick={onClose}>
      <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-photo"
             style={photo ? { backgroundImage: `url(${photo})` } : undefined}>
          {!photo && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
                          width:"100%", height:"100%", fontSize:60, color:"#c8a05a", opacity:.35 }}>
              ◇
            </div>
          )}
          <button className="pp-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="pp-modal-body">
          <h2>Room {room.room_number}</h2>
          <div className="pp-modal-subtitle">
            {room.room_type_name || "Standard"}
            {room.floor ? ` · Floor ${room.floor}` : ""}
            {!bookable && ` · Currently ${STATUS_LABEL[room.status] || room.status}`}
          </div>

          <div className="pp-fact-grid">
            <Fact label="Bed"        value={room.bed_type || "—"} />
            <Fact label="Occupancy"  value={room.max_occupancy ? `${room.max_occupancy} guests` : "—"} />
            <Fact label="View"       value={room.view_type || "—"} />
            <Fact label="Size"       value={room.size_sqm ? `${room.size_sqm} m²` : "—"} />
          </div>

          {amenities.length > 0 && (
            <div className="pp-amenity-row">
              {amenities.map((k) => (
                <span key={k} className="pp-amenity-pill">
                  {AMENITY_LABEL[k] || k}
                </span>
              ))}
            </div>
          )}

          {room.description && <div className="pp-desc">{room.description}</div>}

          {/* Booking form */}
          {!bookable ? (
            <div className="pp-error">
              This room isn't accepting new reservations right now. Please pick another.
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="pp-booking-grid">
                <div className="pp-field">
                  <label className="pp-label">Check-in</label>
                  <input className="pp-input" type="date" required min={todayISO()}
                         value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
                </div>
                <div className="pp-field">
                  <label className="pp-label">Check-out</label>
                  <input className="pp-input" type="date" required min={checkIn}
                         value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
                </div>
                <div className="pp-field">
                  <label className="pp-label">Adults</label>
                  <input className="pp-input" type="number" min="1" max={room.max_occupancy || 10}
                         value={adults} onChange={(e) => setAdults(e.target.value)} />
                </div>
                <div className="pp-field">
                  <label className="pp-label">Children</label>
                  <input className="pp-input" type="number" min="0" max="10"
                         value={children} onChange={(e) => setChildren(e.target.value)} />
                </div>
              </div>

              <div className="pp-field">
                <label className="pp-label">Notes (optional)</label>
                <input className="pp-input" value={notes}
                       onChange={(e) => setNotes(e.target.value)}
                       placeholder="Late arrival, dietary needs, accessibility, …" />
              </div>

              <div className="pp-quote">
                <div>
                  <div className="pp-quote-lbl">Estimated Total</div>
                  <div className="pp-quote-nights">
                    {quote.nights > 0
                      ? `${quote.nights} night${quote.nights === 1 ? "" : "s"} × GH₵ ${Number(room.price_per_night).toFixed(2)}`
                      : "Pick your dates"}
                  </div>
                </div>
                <div className="pp-quote-val">GH₵ {quote.total.toFixed(2)}</div>
              </div>

              {err && <div className="pp-error">{err}</div>}

              {!guest ? (
                <button type="button" className="pp-btn" onClick={onSignIn}>
                  Sign in to reserve
                </button>
              ) : (
                <button type="submit" className="pp-btn" disabled={busy || quote.nights <= 0}>
                  {busy ? "Submitting…" : `Reserve · GH₵ ${quote.total.toFixed(2)}`}
                </button>
              )}

              <div className="pp-form-foot" style={{ marginTop: 14 }}>
                Your reservation will be <strong>pending</strong> until the hotel confirms payment.
                Payment is collected on arrival or via the channels we'll share.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="pp-fact">
      <div className="pp-fact-lbl">{label}</div>
      <div className="pp-fact-val">{value}</div>
    </div>
  );
}

// ─── Post-booking confirmation ───────────────────────────────────────────
function ConfirmationModal({ payload, onClose, onViewMine }) {
  return (
    <div className="pp-modal-overlay" onClick={onClose}>
      <div className="pp-modal" style={{ width: "min(540px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal-body" style={{ padding: "40px 40px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 56, color: "#c8a05a", marginBottom: 6, lineHeight: 1 }}>◇</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, color: "#2c1f14", marginBottom: 8 }}>
            Reservation Submitted
          </h2>
          <div style={{ fontSize: 13.5, color: "#5a4838", fontWeight: 300, lineHeight: 1.6, marginBottom: 24 }}>
            Thank you. Your reservation is <strong>pending confirmation</strong>.
            Once payment is received, we'll lock in your room and you're all set.
          </div>

          <div style={{
            background: "var(--p-dark-2)", color: "#f0e4cf",
            borderRadius: 10, padding: "16px 20px", marginBottom: 18, textAlign: "left",
          }}>
            <div style={{ fontSize: 10.5, letterSpacing: ".2em", textTransform: "uppercase", color: "rgba(200,160,90,.55)" }}>
              Reservation code
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, color: "#c8a05a", margin: "2px 0 10px" }}>
              {payload.code}
            </div>
            <div style={{ fontSize: 12.5, color: "rgba(240,228,207,.75)" }}>
              Room {payload.room_number} · {payload.nights} night{payload.nights === 1 ? "" : "s"}<br/>
              {payload.check_in} → {payload.check_out}<br/>
              Total · GH₵ {Number(payload.total).toFixed(2)}
            </div>
          </div>

          <button className="pp-btn" onClick={onViewMine}>See my reservations</button>
          <div className="pp-form-foot" style={{ marginTop: 14 }}>
            <button onClick={onClose} style={{
              background: "none", border: "none", color: "var(--p-mid)",
              cursor: "pointer", fontFamily: "inherit", fontSize: 13,
            }}>
              Back to rooms
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
